import { ConsumptionType } from "features/consumption/enums/consumptionType";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import { ContactType } from "features/customer/enums/contactType";
import IDefaultDataGridConfiguration from "../components/dataGrid/IDefaultDataGridConfiguration";
import { loadModule, loadPortalConfig } from "features/shared/helpers/modules";
import { SummaryCountByType } from 'features/shared/enums/summaryCountByType';
import { formatISO } from "date-fns";
import { ContractStatus } from "features/contracts/enums/contractStatus";

const defaultGridConfiguration = loadModule<IDefaultDataGridConfiguration>("dataGrids/defaultDataGridConfiguration.json");
const portalConfig = loadPortalConfig();

export const getQueryParams = (name: string, values: string | string[]): URLSearchParams => {
  let queryParams = new URLSearchParams();

  if (Array.isArray(values)) {
    for (const value of values) {
      queryParams.append(name, value);
    }
  } else {
    queryParams.set(name, values);
  }
  
  return queryParams;
}

export const appendQueryParams = (queryParams: URLSearchParams, name: string, values: string | string[]): URLSearchParams => {
  if (Array.isArray(values)) {
    for (const value of values) {
      queryParams.append(name, value);
    }
  } else {
    queryParams.set(name, values);
  }

  return queryParams;
};

export const getPaginationDefaults = (pageSize = defaultGridConfiguration.pageSize, pageNumber = 1) => {
  let queryParams = new URLSearchParams();
  // pagination params
  queryParams.set("Page", pageNumber.toString());
  queryParams.set("PageSize", pageSize.toString());
  
  return queryParams;
}

export const getCustomerContactsParams = (contactType: ContactType, pageSize: number): URLSearchParams => {
  let params = getPaginationDefaults(pageSize);
  params.set("ContactType", contactType);
  
  return params;
}

export const getConsumptionParams = (consumptionType: ConsumptionType, commodityDetail?: CommodityDetail, count?: number, isProduction?: boolean) => {
  let params = new URLSearchParams();
  params.set("ConsumptionType", consumptionType);

  if (commodityDetail) {
    params.set("CommodityDetail", commodityDetail);
  }
  
  if (count) {
    params.set("Count", count.toString());
  }

  if (isProduction) {
    params.set("IsProduction", isProduction.toString());
  }

  return params;
};

export const getContractualAccountsParams = () => {
  // TODO: pokud bude mít zákazník > 100 účtů - nebude fungovat (nezobrazí se všechny)
  return getPaginationDefaults(100);
}

export const getSummaryCountsParams = () => {
  let params = new URLSearchParams();

  if (portalConfig.useContractualAccounts) {
    params.set("CountBy", SummaryCountByType.ContractualAccounts);
  } else {
    params.set("CountBy", SummaryCountByType.Contracts);
  }

  return params;
}

export const getConsumptionRealDetailParams = (
  id: string,
  profileDataType: "Contract" | "PlaceOfDelivery",
  year: number,
  month?: number,
  customerId?: string,
  isProduction?: boolean
): URLSearchParams => {
  let params = new URLSearchParams();

  let from;
  let to;

  if (month) {
    from = new Date(year, month - 1, 1);
    to = new Date(year, month, 0);
  } else {
    from = new Date(year, 0, 1);
    to = new Date(year, 11, 31);
  }

  params.set("From", formatISO(from, { representation: "date" }));
  params.set("To", formatISO(to, { representation: "date" }));

  params.set(`${profileDataType}Id`, id);

  if (profileDataType == "PlaceOfDelivery" && customerId) {
    params.set("CustomerId", customerId);
  }

  if (isProduction) {
    params.set("IsProduction", `${isProduction}`);
  }

  return params;
}

export const getPlaceOfDeliveryContractsDurationParams = (
  customerId?: string,
  placeOfDeliveryId?: string,
  commodityDetails?: CommodityDetail[],
  isProduction?: boolean
) => {
  let params = getQueryParams("ContractStatus", [
    ContractStatus.Effective,
    ContractStatus.Valid,
    ContractStatus.Terminated,
  ]);

  if (customerId) {
    params.set("CustomerId", customerId);
  }
  if (placeOfDeliveryId) {
    params.set("PlaceOfDeliveryId", placeOfDeliveryId);
  }
  if (commodityDetails && commodityDetails?.length > 0) {
    for (const commodity of commodityDetails) {
      params.append("CommodityDetail", commodity);
    }
  }
  if (isProduction) {
    params.set("IsProduction", `${isProduction}`);
  }

  return params;
};

export const getForAdminParam = (forAdminUse: boolean) => {
  if (forAdminUse === true) {
    return getQueryParams("ForAdminUse", forAdminUse.toString());
  }

  return undefined;
}

export const containsFuelingParam = (searchParams: URLSearchParams, paramName: string = "isFueling"): boolean => { 
  let isFueling = searchParams.get(paramName);
  if (isFueling != null) {
    return isFueling === "true";
  }

  return false;
}